import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { getArticleById } from '../data/articles';

const ArticleDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : null;

  if (!article) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            記事が見つかりませんでした
          </h1>
          <Link
            to="/articles"
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            記事一覧に戻る
          </Link>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ja-JP');
  };

  // 動的タイトル生成
  const getTitle = () => {
    if (article) {
      return `${article.title} | 【ニッチな分析】ダッシュボード`
    }
    return '記事詳細 | 【ニッチな分析】ダッシュボード'
  };

  // 動的ディスクリプション生成
  const getDescription = () => {
    if (article) {
      return article.description || `${article.title}の詳細情報をお届けします`
    }
    return '高校サッカーの記事詳細情報をお届けします'
  };

  // 動的OG画像生成
  const getOGImage = () => {
    if (article?.thumbnail) {
      return article.thumbnail
    }
    return 'https://niche-football.com/og-image.jpg' // デフォルト画像
  };

  // 構造化データ生成
  const getStructuredData = () => {
    if (!article) return null
    
    return {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": article.title,
      "description": article.description,
      "image": article.thumbnail,
      "author": {
        "@type": "Organization",
        "name": "【ニッチな分析】ダッシュボード"
      },
      "publisher": {
        "@type": "Organization",
        "name": "【ニッチな分析】ダッシュボード"
      },
      "datePublished": article.publishedAt,
      "dateModified": article.updatedAt || article.publishedAt,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://niche-football.com/article/${article.id}`
      }
    }
  };

  // BreadcrumbList構造化データ生成
  const getBreadcrumbData = () => {
    if (!article) return null
    
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "ホーム",
          "item": "https://niche-football.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "記事・ランキング",
          "item": "https://niche-football.com/articles"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": article.title,
          "item": `https://niche-football.com/article/${article.id}`
        }
      ]
    }
  };

  return (
    <>
      <Helmet>
        <title>{getTitle()}</title>
        <meta name="description" content={getDescription()} />
        
        {/* Open Graph */}
        <meta property="og:title" content={getTitle()} />
        <meta property="og:description" content={getDescription()} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://niche-football.com/article/${article?.id}`} />
        <meta property="og:image" content={getOGImage()} />
        <meta property="og:site_name" content="【ニッチな分析】ダッシュボード" />
        <meta property="og:locale" content="ja_JP" />
        
        {/* Twitter Card */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={getTitle()} />
        <meta property="twitter:description" content={getDescription()} />
        <meta property="twitter:image" content={getOGImage()} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={`https://niche-football.com/article/${article?.id}`} />
        
        {/* 構造化データ */}
        {getStructuredData() && (
          <script type="application/ld+json">
            {JSON.stringify(getStructuredData())}
          </script>
        )}
        
        {/* BreadcrumbList構造化データ */}
        {getBreadcrumbData() && (
          <script type="application/ld+json">
            {JSON.stringify(getBreadcrumbData())}
          </script>
        )}
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* パンくずリスト */}
        <nav className="mb-8">
          <ol className="flex items-center space-x-2 text-sm text-gray-500">
            <li>
              <Link to="/" className="hover:text-gray-700">
                ホーム
              </Link>
            </li>
            <li>
              <span className="mx-2">/</span>
            </li>
            <li>
              <Link to="/articles" className="hover:text-gray-700">
                記事一覧
              </Link>
            </li>
            <li>
              <span className="mx-2">/</span>
            </li>
            <li className="text-gray-900">{article.title}</li>
          </ol>
        </nav>

        {/* 記事ヘッダー */}
        <article className="bg-white rounded-lg shadow-sm overflow-hidden">
          {article.thumbnail && (
            <div className="aspect-video bg-gray-200">
              <img
                src={article.thumbnail}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          
          <div className="p-8">
            {/* メタ情報 */}
            <div className="flex items-center gap-4 mb-6">
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                article.category === 'ranking' ? 'bg-red-100 text-red-800' :
                article.category === 'blog' ? 'bg-blue-100 text-blue-800' :
                'bg-green-100 text-green-800'
              }`}>
                {article.category === 'ranking' ? 'ランキング' :
                 article.category === 'blog' ? 'ブログ' : '分析'}
              </span>
              <span className="text-sm text-gray-500">
                公開日: {formatDate(article.publishedAt)}
              </span>
              {article.updatedAt !== article.publishedAt && (
                <span className="text-sm text-gray-500">
                  更新日: {formatDate(article.updatedAt)}
                </span>
              )}
            </div>

            {/* タイトル */}
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              {article.title}
            </h1>

            {/* 説明 */}
            <p className="text-xl text-gray-600 mb-6">
              {article.description}
            </p>

            {/* YouTube動画 */}
            {article.youtubeUrl && (
              <div className="mb-8 p-6 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  📺 関連動画
                </h3>
                <div className="aspect-video mb-4 bg-gray-200 rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-red-600 rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                    </div>
                    <p className="text-gray-600 font-medium">{article.youtubeTitle || 'YouTube動画'}</p>
                    <p className="text-gray-500 text-sm mt-2">動画を視聴するには下のボタンをクリックしてください</p>
                  </div>
                </div>
                <a
                  href={article.youtubeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                  YouTubeで見る
                </a>
              </div>
            )}

            {/* タグ */}
            <div className="flex flex-wrap gap-2 mb-8">
              {article.tags.map(tag => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                >
                  #{tag}
                </span>
              ))}
            </div>

            {/* 記事コンテンツ */}
            <div className="prose prose-lg max-w-none">
              {article.id === '103rd-national-high-school-soccer-championship-member-ranking' && (
                <div className="ranking-container bg-white rounded-lg shadow-sm p-6 my-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">【第103回全国高校サッカー選手権】代表48校の所属リーグと部員数ランキング（独自調査）</h2>
                  <p className="text-gray-600 mb-6">#高校サッカー選手権 #高校サッカー #部員数<br />
第103回全国高校サッカー選手権大会に出場する48校の出場回数、所属リーグ、部員数をまとめました。<br />
部員数は独自調査のため、誤差があるかもしれません。ご了承ください。</p>
                  
                  <div className="ranking-list space-y-4">
                
                    <div key="48" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        48
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">明誠高</h3>
                        <p className="text-blue-600 font-medium">46人</p>
                        <p className="text-gray-600 text-sm mt-1">【島根】私立 初出場 島根県1部</p>
                      </div>
                    </div>
                  
                    <div key="47" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        47
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">大分鶴崎高</h3>
                        <p className="text-blue-600 font-medium">50人</p>
                        <p className="text-gray-600 text-sm mt-1">【大分】★公立 14年ぶり7回目 大分県1部</p>
                      </div>
                    </div>
                  
                    <div key="46" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        46
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">龍谷富山高</h3>
                        <p className="text-blue-600 font-medium">51人</p>
                        <p className="text-gray-600 text-sm mt-1">【富山】私立 初出場 富山県1部</p>
                      </div>
                    </div>
                  
                    <div key="45" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        45
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">西目高</h3>
                        <p className="text-blue-600 font-medium">59人</p>
                        <p className="text-gray-600 text-sm mt-1">【秋田】★公立 12年ぶり14回目 秋田県1部</p>
                      </div>
                    </div>
                  
                    <div key="44" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        44
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">福井商高</h3>
                        <p className="text-blue-600 font-medium">59人</p>
                        <p className="text-gray-600 text-sm mt-1">【福井】★公立 17年ぶり2回目 福井県1部</p>
                      </div>
                    </div>
                  
                    <div key="43" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        43
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">松山北高</h3>
                        <p className="text-blue-600 font-medium">66人</p>
                        <p className="text-gray-600 text-sm mt-1">【愛媛】★公立 8年ぶり6回目 愛媛県1部</p>
                      </div>
                    </div>
                  
                    <div key="42" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        42
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">徳島市立高</h3>
                        <p className="text-blue-600 font-medium">68人</p>
                        <p className="text-gray-600 text-sm mt-1">【徳島】★公立 3年連続21回目 プリンス四国</p>
                      </div>
                    </div>
                  
                    <div key="41" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        41
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">滝川二高</h3>
                        <p className="text-blue-600 font-medium">73人</p>
                        <p className="text-gray-600 text-sm mt-1">【兵庫】私立 3年ぶり22回目 プリンス関西2部</p>
                      </div>
                    </div>
                  
                    <div key="40" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        40
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">堀越高</h3>
                        <p className="text-blue-600 font-medium">81人</p>
                        <p className="text-gray-600 text-sm mt-1">【東京B】私立 2年連続6回目 東京都1部</p>
                      </div>
                    </div>
                  
                    <div key="39" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        39
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">札幌大谷高</h3>
                        <p className="text-blue-600 font-medium">83人</p>
                        <p className="text-gray-600 text-sm mt-1">【北海道】私立 4年ぶり4回目 プリンス北海道</p>
                      </div>
                    </div>
                  
                    <div key="38" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        38
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">京都橘高</h3>
                        <p className="text-blue-600 font-medium">84人</p>
                        <p className="text-gray-600 text-sm mt-1">【京都】私立 2年連続11回目 プリンス関西1部</p>
                      </div>
                    </div>
                  
                    <div key="37" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        37
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">阪南大高</h3>
                        <p className="text-blue-600 font-medium">84人</p>
                        <p className="text-gray-600 text-sm mt-1">【大阪】私立 3年ぶり3回目 プリンス関西1部</p>
                      </div>
                    </div>
                  
                    <div key="36" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        36
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">寒川高</h3>
                        <p className="text-blue-600 font-medium">84人</p>
                        <p className="text-gray-600 text-sm mt-1">【香川】私立 初出場 プリンス四国</p>
                      </div>
                    </div>
                  
                    <div key="35" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        35
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">新潟明訓高</h3>
                        <p className="text-blue-600 font-medium">85人</p>
                        <p className="text-gray-600 text-sm mt-1">【新潟】私立 9年ぶり7回目 プリンス北信越１部</p>
                      </div>
                    </div>
                  
                    <div key="34" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        34
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">高知高</h3>
                        <p className="text-blue-600 font-medium">89人</p>
                        <p className="text-gray-600 text-sm mt-1">【高知】私立 2年ぶり19回目 高知県1部</p>
                      </div>
                    </div>
                  
                    <div key="33" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        33
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">上田西高</h3>
                        <p className="text-blue-600 font-medium">91人</p>
                        <p className="text-gray-600 text-sm mt-1">【長野】私立 7年ぶり3回目 長野県1部</p>
                      </div>
                    </div>
                  
                    <div key="32" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        32
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">近大和歌山高</h3>
                        <p className="text-blue-600 font-medium">92人</p>
                        <p className="text-gray-600 text-sm mt-1">【和歌山】私立 2年ぶり10回目 和歌山県1部</p>
                      </div>
                    </div>
                  
                    <div key="31" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        31
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">草津東高</h3>
                        <p className="text-blue-600 font-medium">94人</p>
                        <p className="text-gray-600 text-sm mt-1">【滋賀】★公立 3年ぶり13回目 滋賀県1部</p>
                      </div>
                    </div>
                  
                    <div key="30" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        30
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">鹿児島城西高</h3>
                        <p className="text-blue-600 font-medium">96人</p>
                        <p className="text-gray-600 text-sm mt-1">【鹿児島】私立 8年ぶり8回目 プレミアWEST</p>
                      </div>
                    </div>
                  
                    <div key="29" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        29
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">金沢学院大附高</h3>
                        <p className="text-blue-600 font-medium">98人</p>
                        <p className="text-gray-600 text-sm mt-1">【石川】私立 初出場 石川県1部</p>
                      </div>
                    </div>
                  
                    <div key="28" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        28
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">長崎総附高</h3>
                        <p className="text-blue-600 font-medium">98人</p>
                        <p className="text-gray-600 text-sm mt-1">【長崎】私立 2年連続10回目 プリンス九州1部</p>
                      </div>
                    </div>
                  
                    <div key="27" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        27
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">愛工大名電高</h3>
                        <p className="text-blue-600 font-medium">103人</p>
                        <p className="text-gray-600 text-sm mt-1">【愛知】私立 5年ぶり2回目 愛知県1部</p>
                      </div>
                    </div>
                  
                    <div key="26" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        26
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">東海大山形高</h3>
                        <p className="text-blue-600 font-medium">104人</p>
                        <p className="text-gray-600 text-sm mt-1">【山形】私立 10年ぶり2回目 山形県1部</p>
                      </div>
                    </div>
                  
                    <div key="25" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        25
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">専修大北上高</h3>
                        <p className="text-blue-600 font-medium">105人</p>
                        <p className="text-gray-600 text-sm mt-1">【岩手】私立 3年ぶり3回目 プリンス東北</p>
                      </div>
                    </div>
                  
                    <div key="24" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        24
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">奈良育英高</h3>
                        <p className="text-blue-600 font-medium">109人</p>
                        <p className="text-gray-600 text-sm mt-1">【奈良】私立 4年連続17回目 奈良県1部</p>
                      </div>
                    </div>
                  
                    <div key="23" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        23
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">帝京高</h3>
                        <p className="text-blue-600 font-medium">110人</p>
                        <p className="text-gray-600 text-sm mt-1">【東京A】私立 15年ぶり35回目 プリンス関東1部</p>
                      </div>
                    </div>
                  
                    <div key="22" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        22
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">広島国際学院高</h3>
                        <p className="text-blue-600 font-medium">112人</p>
                        <p className="text-gray-600 text-sm mt-1">【広島】私立 2年連続2回目 広島県1部</p>
                      </div>
                    </div>
                  
                    <div key="21" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        21
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">津工高</h3>
                        <p className="text-blue-600 font-medium">116人</p>
                        <p className="text-gray-600 text-sm mt-1">【三重】★公立 2年ぶり4回目 三重県1部</p>
                      </div>
                    </div>
                  
                    <div key="20" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        20
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">尚志高</h3>
                        <p className="text-blue-600 font-medium">118人</p>
                        <p className="text-gray-600 text-sm mt-1">【福島】私立 4年連続15回目 プレミアEAST</p>
                      </div>
                    </div>
                  
                    <div key="19" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        19
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">明秀日立高</h3>
                        <p className="text-blue-600 font-medium">120人</p>
                        <p className="text-gray-600 text-sm mt-1">【茨城】私立 2年連続6回目 茨城県1部</p>
                      </div>
                    </div>
                  
                    <div key="18" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        18
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">那覇西高</h3>
                        <p className="text-blue-600 font-medium">120人</p>
                        <p className="text-gray-600 text-sm mt-1">【沖縄】★公立 4年ぶり18回目 沖縄県1部</p>
                      </div>
                    </div>
                  
                    <div key="17" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        17
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">帝京大可児高</h3>
                        <p className="text-blue-600 font-medium">123人</p>
                        <p className="text-gray-600 text-sm mt-1">【岐阜】私立 6年連続11回目 岐阜県1部</p>
                      </div>
                    </div>
                  
                    <div key="16" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        16
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">米子北高</h3>
                        <p className="text-blue-600 font-medium">124人</p>
                        <p className="text-gray-600 text-sm mt-1">【鳥取】私立 15年連続20回目 プレミアWEST</p>
                      </div>
                    </div>
                  
                    <div key="15" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        15
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">流通経済大柏高</h3>
                        <p className="text-blue-600 font-medium">126人</p>
                        <p className="text-gray-600 text-sm mt-1">【千葉】私立 3年ぶり8回目 プレミアEAST</p>
                      </div>
                    </div>
                  
                    <div key="14" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        14
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">佐賀東高</h3>
                        <p className="text-blue-600 font-medium">130人</p>
                        <p className="text-gray-600 text-sm mt-1">【佐賀】★公立 2年連続14回目 プリンス九州2部</p>
                      </div>
                    </div>
                  
                    <div key="13" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        13
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">東北学院高</h3>
                        <p className="text-blue-600 font-medium">141人</p>
                        <p className="text-gray-600 text-sm mt-1">【宮城】私立 37年ぶり5回目 宮城県1部</p>
                      </div>
                    </div>
                  
                    <div key="12" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        12
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">日章学園高</h3>
                        <p className="text-blue-600 font-medium">144人</p>
                        <p className="text-gray-600 text-sm mt-1">【宮崎】私立 3年連続18回目 プリンス九州1部</p>
                      </div>
                    </div>
                  
                    <div key="11" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        11
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">山梨学院高</h3>
                        <p className="text-blue-600 font-medium">151人</p>
                        <p className="text-gray-600 text-sm mt-1">【山梨】私立 2年ぶり10回目 プリンス関東2部</p>
                      </div>
                    </div>
                  
                    <div key="10" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        10
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">高川学園高</h3>
                        <p className="text-blue-600 font-medium">157人</p>
                        <p className="text-gray-600 text-sm mt-1">【山口】私立 6年連続30回目 プリンス中国</p>
                      </div>
                    </div>
                  
                    <div key="9" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        9
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">岡山学芸館高</h3>
                        <p className="text-blue-600 font-medium">159人</p>
                        <p className="text-gray-600 text-sm mt-1">【岡山】私立 4年連続7回目 プリンス中国</p>
                      </div>
                    </div>
                  
                    <div key="8" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        8
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">東海大相模高</h3>
                        <p className="text-blue-600 font-medium">162人</p>
                        <p className="text-gray-600 text-sm mt-1">【神奈川】私立 初出場 神奈川県1部</p>
                      </div>
                    </div>
                  
                    <div key="7" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        7
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">前橋育英高</h3>
                        <p className="text-blue-600 font-medium">167人</p>
                        <p className="text-gray-600 text-sm mt-1">【群馬】私立 4年連続27回目 プレミアEAST</p>
                      </div>
                    </div>
                  
                    <div key="6" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        6
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">正智深谷高</h3>
                        <p className="text-blue-600 font-medium">170人</p>
                        <p className="text-gray-600 text-sm mt-1">【埼玉】私立 8年ぶり4回目 埼玉県1部</p>
                      </div>
                    </div>
                  
                    <div key="5" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        5
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">静岡学園高</h3>
                        <p className="text-blue-600 font-medium">184人</p>
                        <p className="text-gray-600 text-sm mt-1">【静岡】私立 2年連続15回目 プレミアWEST</p>
                      </div>
                    </div>
                  
                    <div key="4" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        4
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">青森山田高</h3>
                        <p className="text-blue-600 font-medium">208人</p>
                        <p className="text-gray-600 text-sm mt-1">【青森】私立 28年連続30回目 プレミアEAST</p>
                      </div>
                    </div>
                  
                    <div key="3" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        3
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">東福岡高</h3>
                        <p className="text-blue-600 font-medium">216人</p>
                        <p className="text-gray-600 text-sm mt-1">【福岡】私立 3年ぶり23回目 プレミアWEST</p>
                      </div>
                    </div>
                  
                    <div key="2" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        2
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">矢板中央高</h3>
                        <p className="text-blue-600 font-medium">254人</p>
                        <p className="text-gray-600 text-sm mt-1">【栃木】私立 2年連続14回目 プリンス関東1部</p>
                      </div>
                    </div>
                  
                    <div key="1" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        1
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">大津高</h3>
                        <p className="text-blue-600 font-medium">265人</p>
                        <p className="text-gray-600 text-sm mt-1">【熊本】★公立 4年連続21回目 プレミアWEST</p>
                      </div>
                    </div>
                  
                  </div>
                  <p className="text-sm text-gray-500 mt-4">出典: 日本サッカー協会</p>
                  <p className="text-sm text-gray-500 mt-2">最終更新: 2025-01-17</p>
                </div>
              )}
              
              {article.id === '103rd-national-high-school-soccer-championship-j-league-players-ranking' && (
                <div className="ranking-container bg-white rounded-lg shadow-sm p-6 my-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">【第103回全国高校サッカー選手権】出場校別 現役Ｊリーガー数（独自調査）</h2>
                  <p className="text-gray-600 mb-6">#高校サッカー選手権 #Jリーグ #ランキング<br />
第103回高校サッカー選手権大会に出場する48校について、高校別に現役Jリーガー数を調査しました。</p>
                  
                  <div className="ranking-list space-y-4">
                
                    <div key="48" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        48
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">龍谷富山高</h3>
                        <p className="text-blue-600 font-medium">0人</p>
                        <p className="text-gray-600 text-sm mt-1">【富山】私立</p>
                      </div>
                    </div>
                  
                    <div key="47" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        47
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">金沢学院大附高</h3>
                        <p className="text-blue-600 font-medium">0人</p>
                        <p className="text-gray-600 text-sm mt-1">【石川】私立</p>
                      </div>
                    </div>
                  
                    <div key="46" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        46
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">明誠高</h3>
                        <p className="text-blue-600 font-medium">0人</p>
                        <p className="text-gray-600 text-sm mt-1">【島根】私立</p>
                      </div>
                    </div>
                  
                    <div key="45" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        45
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">広島国際学院高</h3>
                        <p className="text-blue-600 font-medium">0人</p>
                        <p className="text-gray-600 text-sm mt-1">【広島】私立</p>
                      </div>
                    </div>
                  
                    <div key="44" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        44
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">愛工大名電高</h3>
                        <p className="text-blue-600 font-medium">0人</p>
                        <p className="text-gray-600 text-sm mt-1">【愛知】私立</p>
                      </div>
                    </div>
                  
                    <div key="43" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        43
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">東海大山形高</h3>
                        <p className="text-blue-600 font-medium">0人</p>
                        <p className="text-gray-600 text-sm mt-1">【山形】私立</p>
                      </div>
                    </div>
                  
                    <div key="42" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        42
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">福井商高</h3>
                        <p className="text-blue-600 font-medium">0人</p>
                        <p className="text-gray-600 text-sm mt-1">【福井】★公立</p>
                      </div>
                    </div>
                  
                    <div key="41" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        41
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">専修大北上高</h3>
                        <p className="text-blue-600 font-medium">0人</p>
                        <p className="text-gray-600 text-sm mt-1">【岩手】私立</p>
                      </div>
                    </div>
                  
                    <div key="40" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        40
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">松山北高</h3>
                        <p className="text-blue-600 font-medium">0人</p>
                        <p className="text-gray-600 text-sm mt-1">【愛媛】★公立</p>
                      </div>
                    </div>
                  
                    <div key="39" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        39
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">近大和歌山高</h3>
                        <p className="text-blue-600 font-medium">0人</p>
                        <p className="text-gray-600 text-sm mt-1">【和歌山】私立</p>
                      </div>
                    </div>
                  
                    <div key="38" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        38
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">西目高</h3>
                        <p className="text-blue-600 font-medium">0人</p>
                        <p className="text-gray-600 text-sm mt-1">【秋田】★公立</p>
                      </div>
                    </div>
                  
                    <div key="37" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        37
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">高知高</h3>
                        <p className="text-blue-600 font-medium">0人</p>
                        <p className="text-gray-600 text-sm mt-1">【高知】私立</p>
                      </div>
                    </div>
                  
                    <div key="36" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        36
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">寒川高</h3>
                        <p className="text-blue-600 font-medium">1人</p>
                        <p className="text-gray-600 text-sm mt-1">【香川】私立 主なJリーガー：家泉 怜依</p>
                      </div>
                    </div>
                  
                    <div key="35" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        35
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">津工業高</h3>
                        <p className="text-blue-600 font-medium">1人</p>
                        <p className="text-gray-600 text-sm mt-1">【三重】★公立 主なJリーガー：東出 壮太</p>
                      </div>
                    </div>
                  
                    <div key="34" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        34
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">東北学院高</h3>
                        <p className="text-blue-600 font-medium">1人</p>
                        <p className="text-gray-600 text-sm mt-1">【宮城】私立 主なJリーガー：佐藤 未勇</p>
                      </div>
                    </div>
                  
                    <div key="33" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        33
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">岡山学芸館高</h3>
                        <p className="text-blue-600 font-medium">1人</p>
                        <p className="text-gray-600 text-sm mt-1">【岡山】私立 主なJリーガー：永田 一真</p>
                      </div>
                    </div>
                  
                    <div key="32" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        32
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">大分鶴崎高</h3>
                        <p className="text-blue-600 font-medium">1人</p>
                        <p className="text-gray-600 text-sm mt-1">【大分】★公立 主なJリーガー：河田 晃兵</p>
                      </div>
                    </div>
                  
                    <div key="31" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        31
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">東海大相模高</h3>
                        <p className="text-blue-600 font-medium">2人</p>
                        <p className="text-gray-600 text-sm mt-1">【神奈川】私立 主なJリーガー：中山 陸など</p>
                      </div>
                    </div>
                  
                    <div key="30" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        30
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">上田西高</h3>
                        <p className="text-blue-600 font-medium">2人</p>
                        <p className="text-gray-600 text-sm mt-1">【長野】私立 主なJリーガー：根本 凌など</p>
                      </div>
                    </div>
                  
                    <div key="29" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        29
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">札幌大谷高</h3>
                        <p className="text-blue-600 font-medium">2人</p>
                        <p className="text-gray-600 text-sm mt-1">【北海道】私立 主なJリーガー：木村 太哉など</p>
                      </div>
                    </div>
                  
                    <div key="28" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        28
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">堀越高</h3>
                        <p className="text-blue-600 font-medium">2人</p>
                        <p className="text-gray-600 text-sm mt-1">【東京B】私立 主なJリーガー：井上 太聖など</p>
                      </div>
                    </div>
                  
                    <div key="27" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        27
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">那覇西高</h3>
                        <p className="text-blue-600 font-medium">2人</p>
                        <p className="text-gray-600 text-sm mt-1">【沖縄】★公立 主なJリーガー：徳元 悠平など</p>
                      </div>
                    </div>
                  
                    <div key="26" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        26
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">徳島市立高</h3>
                        <p className="text-blue-600 font-medium">2人</p>
                        <p className="text-gray-600 text-sm mt-1">【徳島】★公立 主なJリーガー：中川 真など</p>
                      </div>
                    </div>
                  
                    <div key="25" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        25
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">新潟明訓高</h3>
                        <p className="text-blue-600 font-medium">3人</p>
                        <p className="text-gray-600 text-sm mt-1">【新潟】私立 主なJリーガー：石川 慧など</p>
                      </div>
                    </div>
                  
                    <div key="24" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        24
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">奈良育英高</h3>
                        <p className="text-blue-600 font-medium">3人</p>
                        <p className="text-gray-600 text-sm mt-1">【奈良】私立 主なJリーガー：島田 拓海など</p>
                      </div>
                    </div>
                  
                    <div key="23" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        23
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">明秀日立高</h3>
                        <p className="text-blue-600 font-medium">4人</p>
                        <p className="text-gray-600 text-sm mt-1">【茨城】私立 主なJリーガー：高嶋 修也など</p>
                      </div>
                    </div>
                  
                    <div key="22" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        22
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">帝京大可児高</h3>
                        <p className="text-blue-600 font-medium">4人</p>
                        <p className="text-gray-600 text-sm mt-1">【岐阜】私立 主なJリーガー：鈴木 淳之介など</p>
                      </div>
                    </div>
                  
                    <div key="21" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        21
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">米子北高</h3>
                        <p className="text-blue-600 font-medium">4人</p>
                        <p className="text-gray-600 text-sm mt-1">【鳥取】私立 主なJリーガー：昌子 源など</p>
                      </div>
                    </div>
                  
                    <div key="20" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        20
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">高川学園高</h3>
                        <p className="text-blue-600 font-medium">4人</p>
                        <p className="text-gray-600 text-sm mt-1">【山口】私立 主なJリーガー：永石 拓海など</p>
                      </div>
                    </div>
                  
                    <div key="19" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        19
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">草津東高</h3>
                        <p className="text-blue-600 font-medium">5人</p>
                        <p className="text-gray-600 text-sm mt-1">【滋賀】★公立 主なJリーガー：山本 悠樹など</p>
                      </div>
                    </div>
                  
                    <div key="18" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        18
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">佐賀東高</h3>
                        <p className="text-blue-600 font-medium">5人</p>
                        <p className="text-gray-600 text-sm mt-1">【佐賀】★公立 主なJリーガー：中野 嘉大など</p>
                      </div>
                    </div>
                  
                    <div key="17" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        17
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">滝川二高</h3>
                        <p className="text-blue-600 font-medium">5人</p>
                        <p className="text-gray-600 text-sm mt-1">【兵庫】私立 主なJリーガー：清水 圭介など</p>
                      </div>
                    </div>
                  
                    <div key="16" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        16
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">阪南大高</h3>
                        <p className="text-blue-600 font-medium">6人</p>
                        <p className="text-gray-600 text-sm mt-1">【大阪】私立 主なJリーガー：鈴木 章斗など</p>
                      </div>
                    </div>
                  
                    <div key="15" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        15
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">鹿児島城西高</h3>
                        <p className="text-blue-600 font-medium">7人</p>
                        <p className="text-gray-600 text-sm mt-1">【鹿児島】私立 主なJリーガー：大迫 勇也など</p>
                      </div>
                    </div>
                  
                    <div key="14" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        14
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">日章学園高</h3>
                        <p className="text-blue-600 font-medium">7人</p>
                        <p className="text-gray-600 text-sm mt-1">【宮崎】私立 主なJリーガー：髙田 椋汰など</p>
                      </div>
                    </div>
                  
                    <div key="13" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        13
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">正智深谷高</h3>
                        <p className="text-blue-600 font-medium">8人</p>
                        <p className="text-gray-600 text-sm mt-1">【埼玉】私立 主なJリーガー：オナイウ 情滋など</p>
                      </div>
                    </div>
                  
                    <div key="12" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        12
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">長崎総附高</h3>
                        <p className="text-blue-600 font-medium">9人</p>
                        <p className="text-gray-600 text-sm mt-1">【長崎】私立 主なJリーガー：鈴木 冬一など</p>
                      </div>
                    </div>
                  
                    <div key="11" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        11
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">尚志高</h3>
                        <p className="text-blue-600 font-medium">9人</p>
                        <p className="text-gray-600 text-sm mt-1">【福島】私立 主なJリーガー：山岸 祐也など</p>
                      </div>
                    </div>
                  
                    <div key="10" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        10
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">京都橘高</h3>
                        <p className="text-blue-600 font-medium">10人</p>
                        <p className="text-gray-600 text-sm mt-1">【京都】私立 主なJリーガー：小屋松 知哉など</p>
                      </div>
                    </div>
                  
                    <div key="9" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        9
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">帝京高</h3>
                        <p className="text-blue-600 font-medium">10人</p>
                        <p className="text-gray-600 text-sm mt-1">【東京A】私立 主なJリーガー：三浦 颯太など</p>
                      </div>
                    </div>
                  
                    <div key="8" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        8
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">山梨学院高</h3>
                        <p className="text-blue-600 font-medium">11人</p>
                        <p className="text-gray-600 text-sm mt-1">【山梨】私立 主なJリーガー：白崎 凌兵など</p>
                      </div>
                    </div>
                  
                    <div key="7" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        7
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">矢板中央高</h3>
                        <p className="text-blue-600 font-medium">11人</p>
                        <p className="text-gray-600 text-sm mt-1">【栃木】私立 主なJリーガー：松井 蓮之など</p>
                      </div>
                    </div>
                  
                    <div key="6" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        6
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">大津高</h3>
                        <p className="text-blue-600 font-medium">22人</p>
                        <p className="text-gray-600 text-sm mt-1">【熊本】★公立 主なJリーガー：濃野 公人など</p>
                      </div>
                    </div>
                  
                    <div key="5" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        5
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">静岡学園高</h3>
                        <p className="text-blue-600 font-medium">29人</p>
                        <p className="text-gray-600 text-sm mt-1">【静岡】私立 主なJリーガー：関根 大輝など</p>
                      </div>
                    </div>
                  
                    <div key="4" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        4
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">東福岡高</h3>
                        <p className="text-blue-600 font-medium">29人</p>
                        <p className="text-gray-600 text-sm mt-1">【福岡】私立 主なJリーガー：長友 佑都など</p>
                      </div>
                    </div>
                  
                    <div key="3" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        3
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">流通経済大柏高</h3>
                        <p className="text-blue-600 font-medium">32人</p>
                        <p className="text-gray-600 text-sm mt-1">【千葉】私立 主なJリーガー：ジャーメイン良など</p>
                      </div>
                    </div>
                  
                    <div key="2" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        2
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">前橋育英高</h3>
                        <p className="text-blue-600 font-medium">32人</p>
                        <p className="text-gray-600 text-sm mt-1">【群馬】私立 主なJリーガー：渡邊 凌磨など</p>
                      </div>
                    </div>
                  
                    <div key="1" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        1
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">青森山田高</h3>
                        <p className="text-blue-600 font-medium">35人</p>
                        <p className="text-gray-600 text-sm mt-1">【青森】私立 主なJリーガー：柴崎 岳など</p>
                      </div>
                    </div>
                  
                  </div>
                                     <p className="text-sm text-gray-500 mt-4">出典: Jリーグ公式サイト</p>
                   <p className="text-sm text-gray-500 mt-2">最終更新: 2025-01-18</p>
                 </div>
               )}
               
               {article.id === '103rd-national-high-school-soccer-championship-tournament-results-ranking' && (
                 <div className="ranking-container bg-white rounded-lg shadow-sm p-6 my-6">
                   <h2 className="text-2xl font-bold text-gray-900 mb-4">【第103回高校サッカー選手権】出場校別 大会通算＆最高成績（独自調査）</h2>
                   <p className="text-gray-600 mb-6">#高校サッカー選手権 #高校サッカー #ランキング<br />
第103回全国高校サッカー選手権に出場する48校について、出場校別の通算成績と最高成績、出場回数、所属リーグをまとめました。</p>
                   
                   <div className="ranking-list space-y-4">
                 
                     <div key="48" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         48
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">明誠</h3>
                         <p className="text-blue-600 font-medium">--</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：--</p>
                       </div>
                       
                     </div>
                   
                     <div key="47" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         47
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">龍谷富山</h3>
                         <p className="text-blue-600 font-medium">--</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：--</p>
                       </div>
                       
                     </div>
                   
                     <div key="46" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         46
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">寒川</h3>
                         <p className="text-blue-600 font-medium">--</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：--</p>
                       </div>
                       
                     </div>
                   
                     <div key="45" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         45
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">金沢学院大付</h3>
                         <p className="text-blue-600 font-medium">--</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：--</p>
                       </div>
                       
                     </div>
                   
                     <div key="44" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         44
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">東海大相模</h3>
                         <p className="text-blue-600 font-medium">--</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：--</p>
                       </div>
                       
                     </div>
                   
                     <div key="43" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         43
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">愛工大名電</h3>
                         <p className="text-blue-600 font-medium">0勝1敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：未勝利</p>
                       </div>
                       
                     </div>
                   
                     <div key="42" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         42
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">東海大山形</h3>
                         <p className="text-blue-600 font-medium">0勝1敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：未勝利</p>
                       </div>
                       
                     </div>
                   
                     <div key="41" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         41
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">福井商</h3>
                         <p className="text-blue-600 font-medium">0勝1敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：未勝利</p>
                       </div>
                       
                     </div>
                   
                     <div key="40" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         40
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">松山北</h3>
                         <p className="text-blue-600 font-medium">0勝5敗1分</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：2回戦</p>
                       </div>
                       
                     </div>
                   
                     <div key="39" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         39
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">専修大北上</h3>
                         <p className="text-blue-600 font-medium">1勝2敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：2回戦</p>
                       </div>
                       
                     </div>
                   
                     <div key="38" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         38
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">札幌大谷</h3>
                         <p className="text-blue-600 font-medium">1勝3敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：2回戦</p>
                       </div>
                       
                     </div>
                   
                     <div key="37" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         37
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">新潟明訓</h3>
                         <p className="text-blue-600 font-medium">1勝6敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：2回戦</p>
                       </div>
                       
                     </div>
                   
                     <div key="36" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         36
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">広島国際学院</h3>
                         <p className="text-blue-600 font-medium">2勝1敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：ベスト16</p>
                       </div>
                       
                     </div>
                   
                     <div key="35" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         35
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">阪南大</h3>
                         <p className="text-blue-600 font-medium">2勝2敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：ベスト16</p>
                       </div>
                       
                     </div>
                   
                     <div key="34" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         34
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">東北学院</h3>
                         <p className="text-blue-600 font-medium">2勝4敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：ベスト8</p>
                       </div>
                       
                     </div>
                   
                     <div key="33" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         33
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">大分鶴崎</h3>
                         <p className="text-blue-600 font-medium">2勝6敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：ベスト16</p>
                       </div>
                       
                     </div>
                   
                     <div key="32" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         32
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">上田西</h3>
                         <p className="text-blue-600 font-medium">3勝2敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：ベスト4</p>
                       </div>
                       
                     </div>
                   
                     <div key="31" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         31
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">正智深谷</h3>
                         <p className="text-blue-600 font-medium">3勝3敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：ベスト8</p>
                       </div>
                       
                     </div>
                   
                     <div key="30" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         30
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">津工</h3>
                         <p className="text-blue-600 font-medium">4勝3敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：ベスト4</p>
                       </div>
                       
                     </div>
                   
                     <div key="29" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         29
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">西目</h3>
                         <p className="text-blue-600 font-medium">5勝13敗2分</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：ベスト8</p>
                       </div>
                       
                     </div>
                   
                     <div key="28" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         28
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">近大和歌山</h3>
                         <p className="text-blue-600 font-medium">7勝9敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：ベスト16</p>
                       </div>
                       
                     </div>
                   
                     <div key="27" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         27
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">帝京大可児</h3>
                         <p className="text-blue-600 font-medium">8勝10敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：ベスト16</p>
                       </div>
                       
                     </div>
                   
                     <div key="26" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         26
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">高知</h3>
                         <p className="text-blue-600 font-medium">8勝18敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：ベスト8</p>
                       </div>
                       
                     </div>
                   
                     <div key="25" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         25
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">明秀学園日立</h3>
                         <p className="text-blue-600 font-medium">8勝5敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：ベスト8</p>
                       </div>
                       
                     </div>
                   
                     <div key="24" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         24
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">佐賀東</h3>
                         <p className="text-blue-600 font-medium">9勝13敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：ベスト8</p>
                       </div>
                       
                     </div>
                   
                     <div key="23" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         23
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">長崎総大付</h3>
                         <p className="text-blue-600 font-medium">9勝9敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：ベスト8</p>
                       </div>
                       
                     </div>
                   
                     <div key="22" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         22
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">堀越</h3>
                         <p className="text-blue-600 font-medium">10勝5敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：ベスト4</p>
                       </div>
                       
                     </div>
                   
                     <div key="21" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         21
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">岡山学芸館</h3>
                         <p className="text-blue-600 font-medium">10勝5敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：優勝</p>
                       </div>
                       
                     </div>
                   
                     <div key="20" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         20
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">鹿児島城西</h3>
                         <p className="text-blue-600 font-medium">10勝7敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：準優勝</p>
                       </div>
                       
                     </div>
                   
                     <div key="19" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         19
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">京都橘</h3>
                         <p className="text-blue-600 font-medium">11勝10敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：準優勝</p>
                       </div>
                       
                     </div>
                   
                     <div key="18" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         18
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">米子北</h3>
                         <p className="text-blue-600 font-medium">11勝19敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：ベスト8</p>
                       </div>
                       
                     </div>
                   
                     <div key="17" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         17
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">草津東</h3>
                         <p className="text-blue-600 font-medium">12勝12敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：準優勝</p>
                       </div>
                       
                     </div>
                   
                     <div key="16" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         16
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">那覇西</h3>
                         <p className="text-blue-600 font-medium">13勝17敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：ベスト8</p>
                       </div>
                       
                     </div>
                   
                     <div key="15" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         15
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">徳島市立</h3>
                         <p className="text-blue-600 font-medium">13勝20敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：ベスト8</p>
                       </div>
                       
                     </div>
                   
                     <div key="14" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         14
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">日章学園</h3>
                         <p className="text-blue-600 font-medium">14勝17敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：ベスト8</p>
                       </div>
                       
                     </div>
                   
                     <div key="13" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         13
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">奈良育英</h3>
                         <p className="text-blue-600 font-medium">15勝16敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：ベスト4</p>
                       </div>
                       
                     </div>
                   
                     <div key="12" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         12
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">尚志</h3>
                         <p className="text-blue-600 font-medium">18勝14敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：ベスト4</p>
                       </div>
                       
                     </div>
                   
                     <div key="11" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         11
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">山梨学院</h3>
                         <p className="text-blue-600 font-medium">18勝7敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：優勝</p>
                       </div>
                       
                     </div>
                   
                     <div key="10" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         10
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">流経大柏</h3>
                         <p className="text-blue-600 font-medium">20勝6敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：優勝</p>
                       </div>
                       
                     </div>
                   
                     <div key="9" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         9
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">矢板中央</h3>
                         <p className="text-blue-600 font-medium">22勝13敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：ベスト4</p>
                       </div>
                       
                     </div>
                   
                     <div key="8" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         8
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">大津</h3>
                         <p className="text-blue-600 font-medium">24勝20敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：準優勝</p>
                       </div>
                       
                     </div>
                   
                     <div key="7" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         7
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">高川学園</h3>
                         <p className="text-blue-600 font-medium">28勝29敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：ベスト4</p>
                       </div>
                       
                     </div>
                   
                     <div key="6" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         6
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">滝川二</h3>
                         <p className="text-blue-600 font-medium">34勝20敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：優勝</p>
                       </div>
                       
                     </div>
                   
                     <div key="5" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         5
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">静岡学園</h3>
                         <p className="text-blue-600 font-medium">36勝12敗1分</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：優勝(2回)</p>
                       </div>
                       
                     </div>
                   
                     <div key="4" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         4
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">東福岡</h3>
                         <p className="text-blue-600 font-medium">45勝19敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：優勝(3回)</p>
                       </div>
                       
                     </div>
                   
                     <div key="3" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         3
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">前橋育英</h3>
                         <p className="text-blue-600 font-medium">47勝25敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：優勝</p>
                       </div>
                       
                     </div>
                   
                     <div key="2" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         2
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">青森山田</h3>
                         <p className="text-blue-600 font-medium">61勝25敗</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：優勝(4回)</p>
                       </div>
                       
                     </div>
                   
                     <div key="1" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
                       <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                         1
                       </div>
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900">帝京</h3>
                         <p className="text-blue-600 font-medium">78勝27敗6分</p>
                         <p className="text-gray-600 text-sm mt-1">最高成績：優勝</p>
                       </div>
                       
                     </div>
                   
                   </div>
                   <p className="text-sm text-gray-500 mt-4">出典: 日本サッカー協会</p>
                   <p className="text-sm text-gray-500 mt-2">最終更新: 2025-08-17</p>
                 </div>
               )}
            </div>
          </div>
        </article>

        {/* 関連記事 */}
        <div className="mt-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            関連記事
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* ここに関連記事のカードを表示 */}
          </div>
        </div>

        {/* 戻るボタン */}
        <div className="mt-8 text-center">
          <Link
            to="/articles"
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            記事一覧に戻る
          </Link>
        </div>
      </div>
    </div>
    </>
  );
};

export default ArticleDetail;
